import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { API_BASE_URL, adminFetch } from '@/lib/authFetch';
import { toast } from 'sonner';
import { RefreshCw, ScanLine, Camera } from 'lucide-react';
import { ocrConfidenceStyle, isAppleMobileUa } from '@/lib/ocrDisplay';

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

interface EventoRow {
  id: number;
  camera_id: number;
  camera_nome: string | null;
  placa: string;
  data_hora: string | null;
  ocr_confianca_pct: number | null;
  imagem_base64: string | null;
  tipo_veiculo_pt?: string | null;
  cor_detectada_pt?: string | null;
  veiculo_confianca_pct?: number | null;
  localizacao?: string | null;
}

interface RelatorioResponse {
  changed?: boolean;
  ultimo_evento_id?: number;
  placas_lidas?: number;
  periodo_dias: number;
  desde: string;
  total_deteccoes_periodo: number;
  eventos_total: number;
  auditoria_ocr?: {
    total_passagens: number;
    placas_lidas: number;
    falhas_ocr: number;
    taxa_leitura_ocr_pct: number | null;
    ocr_confianca_media_pct: number | null;
    yolo_confianca_media_pct: number | null;
    nota?: string;
  };
  eventos_pagina: number;
  eventos_por_pagina: number;
  eventos_total_paginas: number;
  eventos_recentes: EventoRow[];
}

const GRID_OPTIONS = [12, 24, 36, 48] as const;
const AUTO_REFRESH_MS = 300;
const DEFAULT_GRID_SIZE = 24;
const DEFAULT_DIAS = '7';

function formatDataHoraCurta(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${dd}/${mm}/${yy} ${hh}:${min}:${ss}`;
}

function formatDataHoraLonga(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
}

function eventImageSrc(ev: EventoRow | null | undefined): string | null {
  if (!ev?.imagem_base64) return null;
  return `data:image/jpeg;base64,${ev.imagem_base64}`;
}

function parseApiError(res: Response, fallback: string): Promise<string> {
  return res
    .json()
    .catch(() => ({}))
    .then((err: { detail?: string | { message?: string } }) => {
      if (typeof err.detail === 'string') return err.detail;
      if (err.detail && typeof err.detail === 'object' && 'message' in err.detail) {
        return String(err.detail.message);
      }
      if (res.status === 401) return 'Sessão expirada. Faça login novamente no painel admin.';
      if (res.status >= 500) return 'Erro no servidor. Tente novamente em instantes.';
      return fallback;
    });
}

function InfoField({
  label,
  value,
  className = '',
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className={`rounded-md bg-zinc-800/70 border border-white/5 px-3 py-2 min-w-0 ${className}`}>
      <p className="text-[10px] uppercase tracking-wide text-zinc-500 mb-0.5">{label}</p>
      <p className="text-sm text-white truncate" title={value}>
        {value || '—'}
      </p>
    </div>
  );
}

function placaDisplay(placa: string): string {
  const p = (placa || '').trim().toUpperCase();
  if (!p || p === 'SEMPLACA') return '';
  return p;
}

function CapturaDetalhes({ ev }: { ev: EventoRow }) {
  const ocr = ocrConfidenceStyle(ev.ocr_confianca_pct);
  const img = eventImageSrc(ev);

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-lg bg-zinc-950 ring-1 ring-white/10">
        {img ? (
          <img
            src={img}
            alt={`Captura ${ev.placa}`}
            loading="eager"
            decoding="async"
            className="w-full aspect-[4/3] object-contain bg-black/60"
          />
        ) : (
          <div className="flex aspect-[4/3] flex-col items-center justify-center gap-2 text-zinc-600">
            <Camera className="w-10 h-10 opacity-50" />
            <span className="text-xs">Sem imagem</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-baseline gap-2 min-h-[2.5rem]">
        <p
          className={`font-mono text-2xl sm:text-3xl font-bold tracking-[0.12em] ${
            placaDisplay(ev.placa) ? 'text-protec-gold' : 'text-zinc-600'
          }`}
        >
          {placaDisplay(ev.placa) || '—'}
        </p>
        {ev.ocr_confianca_pct != null ? (
          <Badge variant="outline" className={`text-sm font-medium tabular-nums ${ocr.className}`}>
            OCR {Math.round(Number(ev.ocr_confianca_pct))}%
          </Badge>
        ) : (
          <Badge variant="outline" className="text-sm font-medium border-zinc-500/40 text-zinc-400">
            OCR —
          </Badge>
        )}
      </div>

      <InfoField label="Equipamento" value={ev.camera_nome || `Câmera ${ev.camera_id}`} />

      <div className="grid grid-cols-2 gap-2">
        <InfoField label="Data/Hora" value={formatDataHoraLonga(ev.data_hora)} />
        <InfoField label="Tipo" value={ev.tipo_veiculo_pt || '—'} />
      </div>

      <InfoField label="Cor (visão)" value={ev.cor_detectada_pt || '—'} />

      {ev.localizacao && <InfoField label="Localização" value={ev.localizacao} />}
    </div>
  );
}

const PlateCaptureReport = () => {
  const [diasInput, setDiasInput] = useState(DEFAULT_DIAS);
  const [filtroPlaca, setFiltroPlaca] = useState('');
  const debDias = useDebounced(diasInput, 450);
  const debFiltro = useDebounced(filtroPlaca, 450);

  const [gridSize, setGridSize] = useState<number>(DEFAULT_GRID_SIZE);
  const [reloadToken, setReloadToken] = useState(0);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<RelatorioResponse | null>(null);
  const [pulseNovaCaptura, setPulseNovaCaptura] = useState(false);
  const [capturaModal, setCapturaModal] = useState<EventoRow | null>(null);

  const lastDebouncedQuery = useRef<{ d: string; f: string; g: number } | null>(null);
  const ultimoEventoIdRef = useRef<number | null>(null);
  const ultimoPlacasLidasRef = useRef<number | null>(null);
  const pulseTimerRef = useRef<number | null>(null);
  const fullFetchAbortRef = useRef<AbortController | null>(null);
  const pollAbortRef = useRef<AbortController | null>(null);
  const fetchGenRef = useRef(0);
  const dataRef = useRef<RelatorioResponse | null>(null);

  const fetchData = useCallback(async (silent = false) => {
    if (silent && ultimoEventoIdRef.current == null) return;

    const controller = new AbortController();
    const gen = ++fetchGenRef.current;

    if (silent) {
      pollAbortRef.current?.abort();
      pollAbortRef.current = controller;
    } else {
      fullFetchAbortRef.current?.abort();
      pollAbortRef.current?.abort();
      fullFetchAbortRef.current = controller;
      setLoading((prev) => prev || dataRef.current === null);
    }

    try {
      let d = parseInt(String(debDias).trim(), 10);
      if (Number.isNaN(d) || d < 1) d = 1;
      if (d > 366) d = 366;

      const params = new URLSearchParams();
      params.set('dias', String(d));
      params.set('pagina', '1');
      params.set('por_pagina', String(gridSize));
      params.set('max_imagens', String(gridSize));
      const placaNorm = debFiltro.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      if (placaNorm.length > 0) {
        params.set('placa', placaNorm);
      }
      if (silent && ultimoEventoIdRef.current != null) {
        params.set('ultimo_id', String(ultimoEventoIdRef.current));
        params.set('ultimo_placas_lidas', String(ultimoPlacasLidasRef.current ?? 0));
      }

      const res = await adminFetch(
        `${API_BASE_URL}/admin/relatorio-placas-capturadas?${params.toString()}`,
        { signal: controller.signal }
      );

      if (gen !== fetchGenRef.current) return;

      if (!res.ok) {
        const msg = await parseApiError(res, 'Falha ao carregar capturas');
        if (res.status === 401) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          window.location.href = '/admin/login';
          return;
        }
        throw new Error(msg);
      }

      const json = (await res.json()) as RelatorioResponse;
      if (gen !== fetchGenRef.current) return;

      if (json.changed === false) {
        if (json.placas_lidas != null && json.placas_lidas !== ultimoPlacasLidasRef.current) {
          ultimoPlacasLidasRef.current = json.placas_lidas;
          void fetchData(false);
        }
        return;
      }

      const novoId =
        json.ultimo_evento_id ?? json.eventos_recentes?.[0]?.id ?? 0;
      ultimoEventoIdRef.current = novoId;
      ultimoPlacasLidasRef.current = json.auditoria_ocr?.placas_lidas ?? null;

      setData((prev) => {
        const prevId = prev?.ultimo_evento_id ?? prev?.eventos_recentes?.[0]?.id ?? 0;
        if (silent && novoId !== prevId) {
          setPulseNovaCaptura(true);
          if (pulseTimerRef.current) window.clearTimeout(pulseTimerRef.current);
          pulseTimerRef.current = window.setTimeout(() => setPulseNovaCaptura(false), 2000);
        }
        dataRef.current = json;
        return json;
      });
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      if (gen !== fetchGenRef.current) return;
      if (!silent) {
        toast.error(e instanceof Error ? e.message : 'Erro ao carregar capturas');
        setData(null);
      }
    } finally {
      if (!silent && gen === fetchGenRef.current) setLoading(false);
      if (silent && pollAbortRef.current === controller) pollAbortRef.current = null;
      if (!silent && fullFetchAbortRef.current === controller) fullFetchAbortRef.current = null;
    }
  }, [debDias, debFiltro, gridSize]);

  useEffect(() => {
    const prev = lastDebouncedQuery.current;
    const queryChanged =
      prev === null || prev.d !== debDias || prev.f !== debFiltro || prev.g !== gridSize;
    if (queryChanged) {
      lastDebouncedQuery.current = { d: debDias, f: debFiltro, g: gridSize };
      ultimoEventoIdRef.current = null;
      ultimoPlacasLidasRef.current = null;
    }
    void fetchData(false);
  }, [debDias, debFiltro, gridSize, reloadToken, fetchData]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void fetchData(true);
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [fetchData]);

  useEffect(() => {
    return () => {
      fullFetchAbortRef.current?.abort();
      pollAbortRef.current?.abort();
      if (pulseTimerRef.current) window.clearTimeout(pulseTimerRef.current);
    };
  }, []);

  const handleAtualizar = () => setReloadToken((t) => t + 1);

  const capturas = data?.eventos_recentes ?? [];
  const ultimaCaptura = capturas[0] ?? null;

  // Atualiza modal/detalhe quando OCR preenche placa em captura já aberta
  useEffect(() => {
    if (!capturaModal) return;
    const fresh = capturas.find((c) => c.id === capturaModal.id);
    if (
      fresh &&
      (fresh.placa !== capturaModal.placa ||
        fresh.ocr_confianca_pct !== capturaModal.ocr_confianca_pct)
    ) {
      setCapturaModal(fresh);
    }
  }, [capturas, capturaModal]);

  const totalPeriodo = data?.total_deteccoes_periodo ?? 0;
  const auditoria = data?.auditoria_ocr;

  const descricaoVeiculo = (ev: EventoRow) => {
    const parts = [ev.tipo_veiculo_pt, ev.cor_detectada_pt].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : '—';
  };

  return (
    <div className="space-y-4">
      {auditoria && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <Card className="bg-protec-dark-light border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase text-zinc-500">Passagens</p>
              <p className="text-xl font-semibold text-white tabular-nums">{auditoria.total_passagens}</p>
            </CardContent>
          </Card>
          <Card className="bg-protec-dark-light border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase text-zinc-500">Placas lidas</p>
              <p className="text-xl font-semibold text-white tabular-nums">{auditoria.placas_lidas}</p>
            </CardContent>
          </Card>
          <Card className="bg-protec-dark-light border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase text-zinc-500">Sem placa / pendente</p>
              <p className="text-xl font-semibold text-white tabular-nums">{auditoria.falhas_ocr}</p>
            </CardContent>
          </Card>
          <Card className="bg-protec-dark-light border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase text-zinc-500">Taxa leitura OCR</p>
              <p className="text-xl font-semibold text-white tabular-nums">
                {auditoria.taxa_leitura_ocr_pct != null ? `${auditoria.taxa_leitura_ocr_pct}%` : '—'}
              </p>
              <p className="text-[10px] text-zinc-500 mt-1 leading-snug">
                Confiança média das leituras com placa:{' '}
                {auditoria.ocr_confianca_media_pct != null
                  ? `${Math.round(auditoria.ocr_confianca_media_pct)}%`
                  : '—'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-white text-base">Filtros</CardTitle>
            {data != null && !loading && (
              <span className="text-xs text-gray-400 tabular-nums">
                {totalPeriodo} detecção(ões) no período
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-4">
            <div className="flex flex-col gap-1.5 shrink-0 w-full sm:w-auto">
              <Label className="text-gray-300 text-sm">Período (dias)</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={diasInput}
                onChange={(e) => setDiasInput(e.target.value.replace(/[^\d]/g, ''))}
                onBlur={() => {
                  const t = diasInput.trim();
                  if (t === '') {
                    setDiasInput('1');
                    return;
                  }
                  let d = parseInt(t, 10);
                  if (Number.isNaN(d) || d < 1) setDiasInput('1');
                  else if (d > 366) setDiasInput('366');
                  else setDiasInput(String(d));
                }}
                className="bg-protec-dark border-protec-gold/30 text-white h-10 w-full sm:w-28"
              />
            </div>

            <div className="flex flex-col gap-1.5 flex-1 min-w-0 max-w-md">
              <Label className="text-gray-300 text-sm">Filtrar placa (opcional)</Label>
              <Input
                placeholder="ABC1D23"
                value={filtroPlaca}
                onChange={(e) => setFiltroPlaca(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAtualizar();
                  }
                }}
                className="bg-protec-dark border-protec-gold/30 text-white h-10 font-mono"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 min-h-[520px]">
        <Card
          className={`xl:col-span-5 rounded-xl border border-protec-gold/20 bg-gradient-to-b from-protec-dark-light to-zinc-950/80 shadow-lg shadow-black/20 transition-shadow ${
            pulseNovaCaptura ? 'ring-2 ring-protec-gold/60 shadow-protec-gold/10' : ''
          }`}
          aria-live="polite"
        >
          <CardHeader className="border-b border-protec-gold/10 pb-3">
            <CardTitle className="text-white text-base tracking-tight flex items-center gap-2">
              <ScanLine className="w-4 h-4 text-protec-gold" />
              Informações do Veículo
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            {loading && !ultimaCaptura ? (
              <div className="space-y-3" aria-busy="true" aria-label="Carregando última captura">
                <div className="aspect-[4/3] rounded-lg bg-zinc-800/70 animate-pulse" />
                <div className="h-8 rounded bg-zinc-800/50 animate-pulse" />
                <div className="h-10 rounded bg-zinc-800/40 animate-pulse" />
                <div className="grid grid-cols-2 gap-2">
                  <div className="h-14 rounded bg-zinc-800/40 animate-pulse" />
                  <div className="h-14 rounded bg-zinc-800/40 animate-pulse" />
                </div>
              </div>
            ) : !ultimaCaptura ? (
              <div className="py-16 text-center px-4">
                <Camera className="w-10 h-10 text-zinc-600 mx-auto mb-3 opacity-60" />
                <p className="text-gray-400">Nenhuma captura no período selecionado.</p>
                <p className="text-gray-500 text-xs mt-2">
                  Ajuste o período ou aguarde novas leituras das câmeras.
                </p>
              </div>
            ) : (
              <CapturaDetalhes ev={ultimaCaptura} />
            )}
          </CardContent>
        </Card>

        <Card className="xl:col-span-7 rounded-xl border border-protec-gold/20 bg-gradient-to-b from-protec-dark-light to-zinc-950/80 shadow-lg shadow-black/20">
          <CardHeader className="border-b border-protec-gold/10 pb-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-white text-base tracking-tight">
                Monitoramento por Grid
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={String(gridSize)} onValueChange={(v) => setGridSize(Number(v))}>
                  <SelectTrigger className="h-8 w-[72px] bg-protec-dark border-protec-gold/30 text-white text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-protec-dark border-protec-gold/30">
                    {GRID_OPTIONS.map((n) => (
                      <SelectItem key={n} value={String(n)} className="text-white text-xs">
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-400 hover:text-white"
                  onClick={handleAtualizar}
                  disabled={loading}
                  title="Atualizar agora"
                  aria-label="Atualizar capturas"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            {loading && capturas.length === 0 ? (
              <div
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-3 2xl:grid-cols-4 gap-2"
                aria-busy="true"
                aria-label="Carregando grid"
              >
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="rounded-lg border border-white/5 overflow-hidden">
                    <div className="aspect-video bg-zinc-800/70 animate-pulse" />
                    <div className="p-2 space-y-1.5">
                      <div className="h-3 w-20 bg-zinc-800/60 animate-pulse rounded" />
                      <div className="h-4 w-16 bg-zinc-800/50 animate-pulse rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : capturas.length === 0 ? (
              <div className="py-16 text-center px-4">
                <ScanLine className="w-10 h-10 text-zinc-600 mx-auto mb-3 opacity-60" />
                <p className="text-gray-400">Nenhuma captura para exibir no grid.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-3 2xl:grid-cols-4 gap-2">
                {capturas.map((ev, idx) => {
                  const img = eventImageSrc(ev);
                  const isLatest = idx === 0;
                  return (
                    <button
                      type="button"
                      key={ev.id}
                      onClick={() => setCapturaModal(ev)}
                      className={`group relative overflow-hidden rounded-lg border bg-zinc-900/50 transition text-left w-full cursor-pointer ${
                        isLatest
                          ? 'border-protec-gold/50 ring-1 ring-protec-gold/30'
                          : 'border-white/5 hover:border-protec-gold/25'
                      }`}
                    >
                      <div className="relative aspect-video bg-zinc-950 overflow-hidden">
                        {img ? (
                          <img
                            src={img}
                            alt={`Captura ${ev.placa}`}
                            loading={isAppleMobileUa() ? 'eager' : idx < 6 ? 'eager' : 'lazy'}
                            decoding="async"
                            className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center text-zinc-700">
                            <Camera className="w-6 h-6 opacity-40" />
                          </div>
                        )}
                        {isLatest && (
                          <span className="absolute top-1 left-1 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase bg-protec-gold/90 text-black">
                            Última
                          </span>
                        )}
                      </div>
                      <div className="p-2 space-y-0.5 min-w-0">
                        <p className="text-[10px] text-zinc-500 tabular-nums truncate">
                          {formatDataHoraCurta(ev.data_hora)}
                        </p>
                        <p
                          className={`font-mono text-sm font-semibold tracking-wider truncate min-h-[1.25rem] ${
                            placaDisplay(ev.placa) ? 'text-white' : 'text-zinc-600'
                          }`}
                        >
                          {placaDisplay(ev.placa) || '—'}
                        </p>
                        <p className="text-[10px] text-zinc-400 truncate">{descricaoVeiculo(ev)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={capturaModal != null} onOpenChange={(open) => !open && setCapturaModal(null)}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ScanLine className="w-4 h-4 text-protec-gold" />
              Detalhes da captura
            </DialogTitle>
          </DialogHeader>
          {capturaModal && <CapturaDetalhes ev={capturaModal} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlateCaptureReport;
